import {AfterViewInit, Component, EventEmitter, Input, OnInit, ViewChild} from "@angular/core";
import {
	MatTable,
	MatTableDataSource,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {
	CdkDragDrop,
	moveItemInArray,
	transferArrayItem,
	CdkDropListGroup,
	CdkDropList,
	CdkDrag,
} from "@angular/cdk/drag-drop";
import {TranslateParser, TranslateService, TranslatePipe} from "@ngx-translate/core";
import {Employee} from "../../../../core/models/Employee";
import {MessageService} from "../../../../core/services/services/message.service";
import {TranslatablePaginator} from "../../../../core/models/TranslatablePaginator";
import {EmployeeService} from "../../../../core/services/services/employee.service";
import {debounceTime} from "rxjs/operators";
import {MatFormField} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";

/**
 * Two drag-drop tables with draggable rows.
 * The left table is fetching employees from the backend while the right table is using a local array.
 * The projectLeader cannot be moved.
 */
@Component({
	selector: "app-employee-drag-drop-list",
	templateUrl: "./employee-drag-drop-list.component.html",
	styleUrls: ["./employee-drag-drop-list.component.scss"],
	imports: [
		MatFormField,
		MatInput,
		CdkDropListGroup,
		MatProgressBar,
		MatTable,
		CdkDropList,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		MatIconButton,
		MatIcon,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		CdkDrag,
		MatPaginator,
		TranslatePipe,
	],
})
export class EmployeeDragDropListComponent implements AfterViewInit, OnInit {
	displayedColumns: string[] = ["name", "action"];
	rightDataSource: MatTableDataSource<Employee> = new MatTableDataSource<Employee>();
	leftDataSource: MatTableDataSource<Employee> = new MatTableDataSource<Employee>();

	searchString: string;
	searchStringChanged = new EventEmitter<void>();

	@Input() projectLeader: number;
	@Input() set employeesRight(employees: Employee[]) {
		this.rightDataSource.data = employees;
	}
	@ViewChild("rightTable") rightTable: MatTable<Employee>;
	@ViewChild("rightPaginator") rightPaginator: MatPaginator;

	@ViewChild("leftTable") leftTable: MatTable<Employee>;
	@ViewChild("leftPaginator") leftPaginator: MatPaginator;

	loading = false;

	constructor(
		private _employeeService: EmployeeService,
		private _translate: TranslateService,
		private _parser: TranslateParser,
		private _messageService: MessageService,
	) {
		this.searchStringChanged.pipe(debounceTime(500)).subscribe(() => this.ngOnInit());
	}

	ngOnInit(): void {
		if (this.searchString !== "" && this.searchString !== undefined) {
			this.loading = true;
			let result = this._employeeService.fetchEmployeesfromAD(this.searchString);
			result
				.subscribe((employees) => {
					this.leftDataSource.data = (employees || []).filter((employee) =>
						this.tableDoesntIncludeEmployee(this.rightDataSource.data, employee),
					);
					this.leftDataSource.data.sort(
						(a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName),
					);
					if (this.leftDataSource.paginator) {
						this.leftDataSource.paginator.firstPage();
					}
				})
				.add(() => (this.loading = false));
		}
	}

	ngAfterViewInit(): void {
		this.leftDataSource.paginator = this.leftPaginator;
		this.leftPaginator._intl = new TranslatablePaginator(this._translate, this._parser);
		this.rightDataSource.paginator = this.rightPaginator;
		this.rightPaginator._intl = new TranslatablePaginator(this._translate, this._parser);
	}

	drop(event: CdkDragDrop<Employee[]>, currentIndexOffset: number, previousIndexOffset: number) {
		if (event.previousContainer === event.container) {
			moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex + previousIndexOffset,
				event.currentIndex + currentIndexOffset,
			);
		}
		this.updateTables();
	}

	getPaginatorOffset(paginator: MatPaginator): number {
		return paginator.pageSize * paginator.pageIndex;
	}

	setSearchString(searchString: string) {
		this.searchString = searchString;
		this.searchStringChanged.emit();
	}

	isInRightTable(row: Employee) {
		return !this.tableDoesntIncludeEmployee(this.rightDataSource.data, row);
	}

	leftToRight(row: Employee) {
		transferArrayItem(this.leftDataSource.data, this.rightDataSource.data, this.leftDataSource.data.indexOf(row), 0);

		this.updateTables();
	}

	rightToLeft(row: Employee) {
		transferArrayItem(this.rightDataSource.data, this.leftDataSource.data, this.rightDataSource.data.indexOf(row), 0);

		this.updateTables();
	}

	private updateTables() {
		this.rightDataSource.data = this.rightDataSource.data;
		this.leftDataSource.data = this.leftDataSource.data;
	}

	private tableDoesntIncludeEmployee(table: Employee[], employee: Employee) {
		return table.find((row) => row.id === employee.id) === undefined;
	}
}
